import { useEffect, useState } from "react";
import type { DrBanks, DrStatus, V4Bank } from "./deep-research-api.js";

/** Bank selector (IMPORTANT default, ORIGIN) and the bank's handles with tracked status. The two banks are separate lists; they are never merged. */
export function DeepResearchBankSelector({ banks, bank, onBank }: { readonly banks: DrBanks | null; readonly bank: V4Bank; readonly onBank: (b: V4Bank) => void }) {
  return (
    <section className="card" data-testid="dr-bank">
      <h3>Bank</h3>
      <div role="radiogroup" aria-label="Bank">
        {(["IMPORTANT", "ORIGIN"] as const).map((b) => (
          <label key={b} style={{ marginRight: 16 }}>
            <input type="radio" name="dr-bank" value={b} checked={bank === b} onChange={() => onBank(b)} data-testid={`dr-bank-${b}`} /> {b}
            {b === "IMPORTANT" ? " (default)" : ""} {banks ? `· ${banks.banks[b].handles.length} accounts` : ""}
          </label>
        ))}
      </div>
      {banks ? (
        <p className="muted" data-testid="dr-bank-handles">
          {banks.banks[bank].handles.map((h, i) => (
            <span key={h.handle}>{i ? ", " : ""}@{h.handle}{h.tracked ? "" : " (not tracked yet)"}</span>
          ))}
        </p>
      ) : <p className="muted">Loading…</p>}
    </section>
  );
}

/** add_account.py with every option and its V4 default; V4 shows the cost first ("about 8-15 calls per account"), then Confirm. */
export function AddAccountsForm({ status, banks, bank, onStart }: { readonly status: DrStatus; readonly banks: DrBanks | null; readonly bank: V4Bank; readonly onStart: (params: Record<string, unknown>) => Promise<void> }) {
  const d = status.defaults.add;
  const untracked = banks ? banks.banks[bank].handles.filter((h) => !h.tracked).map((h) => h.handle) : [];
  const [picked, setPicked] = useState<string[] | null>(null);
  // FIX-062 live fix 4: after the banks reload (an Add finished) the choice starts again from the bank's untracked accounts.
  useEffect(() => setPicked(null), [banks, bank]);
  const chosen = picked ?? untracked;
  const [fileText, setFileText] = useState("");
  const [reels, setReels] = useState(d.reels);
  const [maxPages, setMaxPages] = useState(d.maxPages);
  const [refresh, setRefresh] = useState(d.refresh);
  const [workers, setWorkers] = useState(d.workers);
  const [confirming, setConfirming] = useState(false);
  const pasted = fileText.split("\n").map((l) => l.trim()).filter((l) => l && !l.startsWith("#")).length;
  const n = chosen.length + pasted;
  return (
    <div className="card" data-testid="dr-add">
      <h4>Add accounts <span className="muted">(add_account.py)</span></h4>
      <p className="muted">Accounts to add: this bank's untracked accounts, plus any pasted handles or links (one per line, # comments skipped). New accounts join the {bank} bank.</p>
      {(banks?.banks[bank].handles ?? []).map((h) => (
        <label key={h.handle} style={{ marginRight: 12 }}>
          <input type="checkbox" checked={chosen.includes(h.handle)} onChange={(e) => setPicked(e.target.checked ? [...chosen, h.handle] : chosen.filter((x) => x !== h.handle))} />
          @{h.handle}{h.tracked ? " (tracked)" : ""}
        </label>
      ))}
      <textarea aria-label="Paste handles or links" rows={3} style={{ width: "100%" }} value={fileText} onChange={(e) => setFileText(e.target.value)} placeholder="@handle or https://www.instagram.com/handle/" />
      <div className="dr-options">
        <label>Stop after N reels (--reels) <input type="number" min={1} value={reels} onChange={(e) => setReels(Number(e.target.value))} /></label>
        <label>Pages per endpoint (--max-pages) <input type="number" min={1} value={maxPages} onChange={(e) => setMaxPages(Number(e.target.value))} /></label>
        <label><input type="checkbox" checked={refresh} onChange={(e) => setRefresh(e.target.checked)} /> Re-pull tracked accounts (--refresh)</label>
        <label>Workers (--workers) <input type="number" min={1} max={8} value={workers} onChange={(e) => setWorkers(Number(e.target.value))} /></label>
      </div>
      {!confirming ? (
        <button type="button" disabled={!status.isAdmin || n === 0} onClick={() => setConfirming(true)} data-testid="dr-add-preview">Preview</button>
      ) : (
        <p data-testid="dr-add-confirm">
          {n} account{n === 1 ? "" : "s"}: about {8 * n}–{15 * n} RapidAPI calls (8–15 per account).{" "}
          <button type="button" onClick={() => { setConfirming(false); void onStart({ handles: chosen, fileText: fileText || null, reels, maxPages, refresh, workers }); }}>Confirm</button>{" "}
          <button type="button" onClick={() => setConfirming(false)}>Cancel</button>
        </p>
      )}
    </div>
  );
}
